import { logger } from './logger';

/**
 * Safely parses JSON string with error handling and optional default value
 * @param jsonString - The JSON string to parse
 * @param defaultValue - The default value to return if parsing fails
 * @returns Parsed JSON object or default value
 */
export const safeJsonParse = <T = any>(
  jsonString: string | undefined | null,
  defaultValue: T
): T => {
  if (!jsonString) return defaultValue;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    logger.jsonParseError(
      jsonString, 
      error as Error, 
      { 
        component: 'safeJsonParse' 
      }
    );
    return defaultValue;
  }
};

/**
 * Safely parses a JSON string that should contain an array
 * @param jsonString - The JSON string to parse
 * @param defaultValue - The default array to return if parsing fails
 * @returns Parsed array or default value
 */
export const safeJsonParseArray = <T = any>(
  jsonString: string | undefined | null,
  defaultValue: T[] = []
): T[] => {
  const parsed = safeJsonParse(jsonString, defaultValue);
  return Array.isArray(parsed) ? parsed : defaultValue;
};

/**
 * Safely parses function arguments from tool calls
 * @param args - The arguments string to parse
 * @returns Parsed arguments object or empty object
 */
export const safeParseToolArgs = (args: string | undefined): Record<string, any> => {
  return safeJsonParse(args || '{}', {});
}; 